import React, { useState } from 'react';
import { useSnapshot } from 'valtio';
import { useNavigate } from 'react-router-dom';
import { state } from '../store/global';
import API from '../lib/api';

import TransferHeader from '../components/common/TransferHeader';
import Inputbox from '../components/common/Inputbox';
import NormalButton from '../components/common/NormalButton';
import LoadingOverlay from '../components/common/LoadingOverlay';

export default function SendToUser() {
  const snap = useSnapshot(state);
  const navigate = useNavigate();

  // Steps: 0 = enter email, 1 = enter amount, 2 = confirm, 3 = success
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Form data
  const [email, setEmail] = useState('');
  const [recipient, setRecipient] = useState(null);
  const [amount, setAmount] = useState('');
  const [concept, setConcept] = useState('');
  const [result, setResult] = useState(null);

  // ── Step 0: Validate recipient email ──────────────────────────
  const handleValidateRecipient = async () => {
    if (!email.trim()) {
      setError('Ingresa el email del destinatario');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const res = await API.post('transaction/validateFlowexPayRecipient', {
        email: email.trim().toLowerCase(),
      });

      if (res.data.valid) {
        setRecipient(res.data.recipient);
        setStep(1);
      }
    } catch (err) {
      const msg = err?.response?.data?.message || 'Error validando destinatario';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  // ── Step 1 → 2: Validate amount and go to confirm ────────────
  const handleContinueToConfirm = () => {
    const numAmount = parseFloat(amount);
    if (isNaN(numAmount) || numAmount <= 0) {
      setError('Ingresa un monto mayor a 0');
      return;
    }
    if (numAmount < 0.01) {
      setError('El monto minimo es 0.01 USDC');
      return;
    }
    setError('');
    setStep(2);
  };

  // ── Step 2: Send transfer ─────────────────────────────────────
  const handleSendTransfer = async () => {
    setLoading(true);
    setError('');

    try {
      const res = await API.post('transaction/transferToFlowexPayUser', {
        recipientEmail: email.trim().toLowerCase(),
        amount: parseFloat(amount),
        concept: concept,
      });

      setResult(res.data.transaction);
      setStep(3);
    } catch (err) {
      const msg = err?.response?.data?.message || 'Error al enviar la transferencia';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  // ── Go back logic ─────────────────────────────────────────────
  const goBack = () => {
    if (step === 0) {
      navigate('/dashboard');
    } else {
      setError('');
      setStep(step - 1);
    }
  };

  // ── Format helpers ────────────────────────────────────────────
  const formatAmount = (value) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  return (
    <div className="py-8 px-6 space-y-6 max-w-lg mx-auto">
      {loading && <LoadingOverlay />}

      {/* ── Step 0: Enter recipient email ───────────────────────── */}
      {step === 0 && (
        <div className="space-y-6">
          <TransferHeader
            headerTitle="Enviar a usuario FlowexPay"
            description="Envia USDC a otro usuario de FlowexPay de forma inmediata y sin costo."
            goBack={goBack}
          />

          <div className="bg-white border border-[#E6EBF7] rounded-[20px] p-6 space-y-4">
            <Inputbox
              title="Email del destinatario"
              placeholder="ejemplo@correo.com"
              value={email}
              onChange={(val) => {
                setEmail(val);
                setError('');
              }}
            />

            {error && (
              <p className="text-xs text-red-500 font-[poppins]">{error}</p>
            )}

            <div className="pt-2">
              <NormalButton
                text="Continuar"
                onClick={handleValidateRecipient}
                disabled={!email.trim() || loading}
                customClass="w-full bg-[#2A59C6] text-white hover:bg-[#1e4bb0] disabled:opacity-50"
              />
            </div>
          </div>

          <div className="bg-[#F0F4FF] border border-[#E6EBF7] rounded-[16px] p-4 space-y-2">
            <p className="text-xs font-semibold text-[#2A59C6] font-[poppins]">
              Transferencias internas
            </p>
            <p className="text-xs text-[#535862] font-[poppins] leading-[18px]">
              Las transferencias entre usuarios FlowexPay son procesadas por Fireblocks en la red Solana.
              El costo de red es menor a $0.001 USD y es asumido por FlowexPay. No se cobra comision.
            </p>
          </div>
        </div>
      )}

      {/* ── Step 1: Enter amount ─────────────────────────────────── */}
      {step === 1 && recipient && (
        <div className="space-y-6">
          <TransferHeader
            headerTitle="Monto a enviar"
            description={`Enviando a ${recipient.firstName} ${recipient.lastName}`}
            goBack={goBack}
          />

          {/* Recipient card */}
          <div className="bg-[#F0F4FF] border border-[#E6EBF7] rounded-[16px] p-4 flex items-center space-x-3">
            <div className="w-10 h-10 bg-[#2A59C6] rounded-full flex items-center justify-center">
              <span className="text-white font-semibold text-sm">
                {(recipient.firstName?.[0] || '').toUpperCase()}
                {(recipient.lastName?.[0] || '').toUpperCase()}
              </span>
            </div>
            <div>
              <p className="text-sm font-semibold text-[#181D27] font-[poppins]">
                {recipient.firstName} {recipient.lastName}
              </p>
              <p className="text-xs text-[#535862] font-[poppins]">
                {recipient.email}
              </p>
            </div>
          </div>

          <div className="bg-white border border-[#E6EBF7] rounded-[20px] p-6 space-y-4">
            <Inputbox
              title="Monto (USDC)"
              placeholder="0.00"
              value={amount}
              onChange={(val) => {
                // Only allow numbers and decimal point
                const cleaned = val.replace(/[^0-9.]/g, '');
                setAmount(cleaned);
                setError('');
              }}
            />

            {error && (
              <p className="text-xs text-red-500 font-[poppins]">{error}</p>
            )}

            <div>
              <label className="block text-xs font-medium text-[#414651] font-[poppins] mb-1">
                Concepto / Referencia (opcional)
              </label>
              <textarea
                value={concept}
                onChange={(e) => setConcept(e.target.value)}
                placeholder="Ej: Pago factura #123 - Cliente María López"
                maxLength={255}
                rows={2}
                className="w-full px-3 py-2 border border-[#D5D7DA] rounded-lg text-sm font-[poppins] resize-none focus:outline-none focus:border-[#2A59C6]"
              />
              <span className="text-[10px] text-[#717680] font-[poppins]">
                {concept.length}/255 — El destinatario verá este concepto
              </span>
            </div>

            <div className="flex items-center justify-between text-xs text-[#535862] font-[poppins]">
              <span>Comision FlowexPay</span>
              <span className="text-[#2A59C6] font-semibold">$0.00 USD</span>
            </div>

            <div className="pt-2">
              <NormalButton
                text="Continuar"
                onClick={handleContinueToConfirm}
                disabled={!amount || loading}
                customClass="w-full bg-[#2A59C6] text-white hover:bg-[#1e4bb0] disabled:opacity-50"
              />
            </div>
          </div>
        </div>
      )}

      {/* ── Step 2: Confirm transfer ─────────────────────────────── */}
      {step === 2 && recipient && (
        <div className="space-y-6">
          <TransferHeader
            headerTitle="Confirmar transferencia"
            description="Revisa los detalles antes de enviar."
            goBack={goBack}
          />

          <div className="bg-white border border-[#E6EBF7] rounded-[20px] p-6 space-y-5">
            {/* Amount */}
            <div className="text-center space-y-1">
              <p className="text-3xl font-bold text-[#181D27] font-[poppins]">
                ${formatAmount(parseFloat(amount))}
              </p>
              <p className="text-sm text-[#535862] font-[poppins]">USDC</p>
            </div>

            <div className="border-t border-[#E6EBF7]"></div>

            {/* Details */}
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-xs text-[#535862] font-[poppins]">Destinatario</span>
                <span className="text-xs font-semibold text-[#181D27] font-[poppins]">
                  {recipient.firstName} {recipient.lastName}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-xs text-[#535862] font-[poppins]">Email</span>
                <span className="text-xs text-[#181D27] font-[poppins]">{recipient.email}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-xs text-[#535862] font-[poppins]">Red</span>
                <span className="text-xs text-[#181D27] font-[poppins]">Solana</span>
              </div>
              <div className="flex justify-between">
                <span className="text-xs text-[#535862] font-[poppins]">Comision</span>
                <span className="text-xs text-[#2A59C6] font-semibold font-[poppins]">Gratis</span>
              </div>
              {concept && (
                <div className="flex justify-between">
                  <span className="text-xs text-[#535862] font-[poppins]">Concepto</span>
                  <span className="text-xs text-[#181D27] font-[poppins] text-right max-w-[200px]">{concept}</span>
                </div>
              )}
            </div>

            <div className="border-t border-[#E6EBF7]"></div>

            <div className="flex justify-between">
              <span className="text-sm font-semibold text-[#181D27] font-[poppins]">Total</span>
              <span className="text-sm font-bold text-[#181D27] font-[poppins]">
                ${formatAmount(parseFloat(amount))} USDC
              </span>
            </div>

            {error && (
              <p className="text-xs text-red-500 font-[poppins]">{error}</p>
            )}

            <div className="space-y-2 pt-2">
              <NormalButton
                text="Confirmar y enviar"
                onClick={handleSendTransfer}
                disabled={loading}
                customClass="w-full bg-[#2A59C6] text-white hover:bg-[#1e4bb0] disabled:opacity-50"
              />
              <NormalButton
                text="Cancelar"
                onClick={goBack}
                customClass="w-full bg-white text-[#535862] border border-[#C2C2C2] hover:bg-[#F5F5F5]"
              />
            </div>
          </div>
        </div>
      )}

      {/* ── Step 3: Success ──────────────────────────────────────── */}
      {step === 3 && result && (
        <div className="space-y-6">
          <div className="text-center space-y-4 pt-8">
            {/* Success check */}
            <div className="w-16 h-16 bg-[#DCFCE7] rounded-full flex items-center justify-center mx-auto">
              <svg className="w-8 h-8 text-[#16A34A]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>

            <div className="space-y-1">
              <p className="text-lg font-bold text-[#181D27] font-[poppins]">
                Transferencia enviada
              </p>
              <p className="text-sm text-[#535862] font-[poppins]">
                Tu transferencia esta siendo procesada por Fireblocks.
              </p>
            </div>
          </div>

          <div className="bg-white border border-[#E6EBF7] rounded-[20px] p-6 space-y-3">
            <div className="flex justify-between">
              <span className="text-xs text-[#535862] font-[poppins]">Monto</span>
              <span className="text-xs font-semibold text-[#181D27] font-[poppins]">
                ${formatAmount(result.amount)} {result.currency}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-xs text-[#535862] font-[poppins]">Destinatario</span>
              <span className="text-xs text-[#181D27] font-[poppins]">
                {result.recipient.name}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-xs text-[#535862] font-[poppins]">Estado</span>
              <span className="text-xs font-semibold text-[#F59E0B] font-[poppins]">
                Procesando
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-xs text-[#535862] font-[poppins]">Comision</span>
              <span className="text-xs text-[#2A59C6] font-semibold font-[poppins]">$0.00</span>
            </div>
          </div>

          <div className="space-y-2">
            <NormalButton
              text="Volver al inicio"
              onClick={() => navigate('/dashboard')}
              customClass="w-full bg-[#2A59C6] text-white hover:bg-[#1e4bb0]"
            />
            <NormalButton
              text="Ver historial"
              onClick={() => navigate('/dashboard/history')}
              customClass="w-full bg-white text-[#535862] border border-[#C2C2C2] hover:bg-[#F5F5F5]"
            />
          </div>
        </div>
      )}
    </div>
  );
}
